import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";

export default function HeroCarousel({ slides }: { slides: NewsItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((v) => (v + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-100 text-sm text-foreground-500 md:h-[420px]">
        暂无最新资讯
      </div>
    );
  }

  const go = (dir: number) => setActive((v) => (v + dir + slides.length) % slides.length);

  return (
    <div className="group relative h-[260px] w-full overflow-hidden rounded-xl border border-background-200 md:h-[420px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={slide.cover}
            alt={`${slide.title} 资讯焦点图`}
            title={`${slide.title} - 黑桃视频最新影视资讯`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/95 via-background-950/50 to-background-950/20"></div>
          <div className="absolute bottom-0 left-0 w-full p-5 md:p-8">
            <span className="inline-block rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-500 text-background-50">
              {slide.tag}
            </span>
            <h2 className="mt-3 max-w-3xl text-lg font-700 leading-snug text-background-950 md:text-2xl">
              {slide.title}
            </h2>
            <p className="mt-2 hidden max-w-2xl text-sm text-foreground-800 md:block">{slide.summary}</p>
            <Link
              to={`/detail/${slide.movieId}`}
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              查看详情
              <i className="ri-arrow-right-line text-[16px]" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-950/50 text-foreground-900 opacity-0 transition-opacity hover:bg-background-950/80 group-hover:opacity-100 md:flex"
      >
        <i className="ri-arrow-left-s-line text-[22px]" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-950/50 text-foreground-900 opacity-0 transition-opacity hover:bg-background-950/80 group-hover:opacity-100 md:flex"
      >
        <i className="ri-arrow-right-s-line text-[22px]" aria-hidden="true"></i>
      </button>

      <div className="absolute bottom-4 right-5 flex items-center gap-1.5 md:bottom-8 md:right-8">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => setActive(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === active ? "w-6 bg-primary-500" : "w-1.5 bg-background-800"
            }`}
          />
        ))}
      </div>
    </div>
  );
}